import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Shield, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { getAccessToken } from '../../utils/auth';
import { SEO } from '../../components/SEO';
import { siteConfig } from '../../config/site';

interface ClaimantProfile {
  email: string | null;
  full_name: string | null;
}

interface ClaimBusiness {
  id: string;
  business_id: string;
  user_id: string;
  evidence: string | null;
  status: 'pending' | 'approved' | 'denied';
  created_at: string;
  businesses?: {
    id: string;
    name: string;
  } | null;
  profiles?: ClaimantProfile | null;
}

export function ClaimApprovals() {
  const { session } = useAuth();
  const [claims, setClaims] = useState<ClaimBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    let isMounted = true;

    const fetchClaims = async () => {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('business_claims')
        .select('id, business_id, user_id, evidence, status, created_at, businesses(id, name), profiles!user_id(email, full_name)')
        .eq('status', 'pending')
        .order('created_at', { ascending: true });

      if (!isMounted) return;

      if (fetchError) {
        setError(fetchError.message || 'Failed to load pending claims');
        setClaims([]);
      } else {
        setClaims((data as ClaimBusiness[]) || []);
      }
      setLoading(false);
    };

    void fetchClaims();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAction = async (claimId: string, action: 'approve' | 'deny') => {
    const notes = adminNotes[claimId]?.trim();

    if (action === 'deny' && !notes) {
      setActionErrors(prev => ({
        ...prev,
        [claimId]: 'Please provide admin notes for this denial.',
      }));
      return;
    }

    setActionLoading(prev => ({ ...prev, [claimId]: true }));
    setActionErrors(prev => ({ ...prev, [claimId]: '' }));

    const previous = claims;
    setClaims(prev => prev.filter(claim => claim.id !== claimId));

    try {
      const token = await getAccessToken(session?.access_token);
      if (!token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('/api/admin/manage-claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          claimId,
          action,
          adminNotes: action === 'deny' ? notes : undefined,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload?.error) {
        const message = payload?.error || 'Failed to update claim';
        throw new Error(typeof message === 'string' ? message : 'Failed to update claim');
      }

      setAdminNotes(prev => {
        const next = { ...prev };
        delete next[claimId];
        return next;
      });
    } catch (err) {
      setClaims(previous);
      const message = err instanceof Error ? err.message : 'Failed to update claim';
      setActionErrors(prev => ({ ...prev, [claimId]: message }));
    } finally {
      setActionLoading(prev => ({ ...prev, [claimId]: false }));
    }
  };

  const formattedClaims = useMemo(
    () =>
      claims.map(claim => ({
        ...claim,
        submittedDate: new Date(claim.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        claimantName: claim.profiles?.full_name || 'Unknown claimant',
        claimantEmail: claim.profiles?.email || 'No email provided',
      })),
    [claims]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-surface py-12">
        <SEO
          title={siteConfig.seo.pages.adminTitle}
          path="/admin/claims"
          noindex
        />
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-on-surface-variant animate-spin mx-auto mb-4" />
            <p className="text-on-surface-variant">Loading pending claims...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface py-12">
      <SEO
        title={siteConfig.seo.pages.adminTitle}
        path="/admin/claims"
        noindex
      />
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-on-surface">Claim Approvals</h1>
            <p className="text-on-surface-variant">Review and approve business ownership claims.</p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-primary-container px-4 py-2 text-sm font-medium text-on-primary-container">
            <Shield className="w-4 h-4" />
            Ownership review
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-error bg-error-container p-4 text-on-error-container text-sm">
            {error}
          </div>
        )}

        {formattedClaims.length === 0 ? (
          <div className="card text-center text-on-surface-variant">
            No pending claims 🎉
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {formattedClaims.map(claim => (
              <div key={claim.id} className="card">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div>
                    <h2 className="text-xl font-semibold text-on-surface mb-1">
                      {claim.businesses?.name || 'Unknown business'}
                    </h2>
                    <p className="text-sm text-on-surface-variant mb-4">
                      Submitted {claim.submittedDate}
                    </p>
                    <div className="space-y-2 text-sm text-on-surface-variant">
                      <p>
                        <span className="text-on-surface">Claimant:</span> {claim.claimantName} ({claim.claimantEmail})
                      </p>
                      <p>
                        <span className="text-on-surface">Business:</span>{' '}
                        <Link
                          to={`/businesses/${claim.businesses?.id || claim.business_id}`}
                          className="text-primary hover:underline"
                        >
                          View listing
                        </Link>
                      </p>
                      <p className="text-on-surface">
                        Evidence:
                      </p>
                      <p className="rounded-xl border border-outline-variant bg-surface-container-high p-3 text-on-surface-variant whitespace-pre-line">
                        {claim.evidence || 'No evidence provided.'}
                      </p>
                    </div>
                  </div>

                  <div className="w-full lg:w-80 space-y-3">
                    <textarea
                      rows={3}
                      placeholder="Admin notes for denial"
                      value={adminNotes[claim.id] || ''}
                      onChange={(event) =>
                        setAdminNotes(prev => ({
                          ...prev,
                          [claim.id]: event.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-outline bg-surface px-4 py-3 text-sm text-on-surface"
                    />

                    {actionErrors[claim.id] && (
                      <div className="rounded-xl border border-error bg-error-container p-3 text-on-error-container text-xs">
                        {actionErrors[claim.id]}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={() => handleAction(claim.id, 'approve')}
                        className="btn-primary flex items-center justify-center gap-2"
                        disabled={actionLoading[claim.id]}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAction(claim.id, 'deny')}
                        className="rounded-full px-4 py-2 font-medium border border-error text-error hover:bg-error-container transition-colors duration-[var(--md-sys-motion-duration-short3)]"
                        disabled={actionLoading[claim.id]}
                      >
                        <span className="inline-flex items-center gap-2">
                          <XCircle className="w-4 h-4" />
                          Deny
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
